import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  $getRoot,
  FORMAT_ELEMENT_COMMAND,
  $createParagraphNode,
  $createTextNode,
  createCommand,
  COMMAND_PRIORITY_NORMAL,
  $isTextNode,
} from 'lexical'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode, HeadingTagType } from '@lexical/rich-text'
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode'
import { $createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import React, { useCallback, useEffect, useState } from 'react'
import { $createTableNodeWithDimensions } from '@lexical/table'
import styled from 'styled-components'

const SET_FONT_SIZE_COMMAND = createCommand('SET_FONT_SIZE')
const SET_FONT_FAMILY_COMMAND = createCommand('SET_FONT_FAMILY')

const fontSizes = [
  { label: '10', size: '10' },
  { label: '12', size: '12' },
  { label: '14', size: '14' },
  { label: '16', size: '16' },
  { label: '18', size: '18' },
  { label: '20', size: '20' },
  { label: '24', size: '24' },
  { label: '28', size: '28' },
  { label: '32', size: '32' },
  { label: '36', size: '36' },
]

const fontFamilies = [
  { label: '기본', value: 'Spoqa Han Sans Neo' },
  { label: '굴림', value: 'Gulim' },
  { label: '돋움', value: 'Dotum' },
  { label: '바탕', value: 'Batang' },
  { label: '맑은 고딕', value: 'Malgun Gothic' },
  { label: '나눔고딕', value: 'Nanum Gothic' },
  { label: '나눔명조', value: 'Nanum Myeongjo' },
]

const ToolbarPlugin = () => {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [isCode, setIsCode] = useState(false)
  const [isLink, setIsLink] = useState(false)
  const [fontSize, setFontSize] = useState('16')
  const [fontFamily, setFontFamily] = useState('Spoqa Han Sans Neo')

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsCode(selection.hasFormat('code'))

      // 링크 상태 확인
      const nodes = selection.getNodes()
      const hasLink = nodes.some(node => {
        const parent = node.getParent()
        return parent ? $isLinkNode(parent) : false
      })
      setIsLink(hasLink)
    }
  }, [])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  useEffect(() => {
    editor.registerCommand(
      SET_FONT_SIZE_COMMAND,
      (size: string) => {
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const nodes = selection.getNodes()
            nodes.forEach((node) => {
              if ($isTextNode(node)) {
                const textContent = node.getTextContent()
                const textNode = $createTextNode(textContent)
                textNode.setStyle(`font-size: ${size}px`)
                node.replace(textNode)
              }
            })
          }
        })
        return true
      },
      COMMAND_PRIORITY_NORMAL
    )
  }, [editor])

  useEffect(() => {
    editor.registerCommand(
      SET_FONT_FAMILY_COMMAND,
      (font: string) => {
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const nodes = selection.getNodes()
            nodes.forEach((node) => {
              if ($isTextNode(node)) {
                const textContent = node.getTextContent()
                const textNode = $createTextNode(textContent)
                textNode.setStyle(`font-family: ${font}`)
                node.replace(textNode)
              }
            })
          }
        })
        return true
      },
      COMMAND_PRIORITY_NORMAL
    )
  }, [editor])

  const formatHeading = useCallback(
    (type: HeadingTagType) => {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(type))
        }
      })
    },
    [editor]
  )

  const insertHorizontalRule = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const horizontalRuleNode = new HorizontalRuleNode()
        selection.insertNodes([horizontalRuleNode])
      }
    })
  }, [editor])

  const insertLink = useCallback(() => {
    if (!isLink) {
      const url = prompt('링크를 입력하세요:')
      if (url !== null) {
        // URL에 프로토콜이 없는 경우 https:// 추가
        const urlWithProtocol = url.match(/^https?:\/\//) ? url : `https://${url}`
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
          url: urlWithProtocol,
          target: '_blank',
          rel: 'noopener noreferrer'
        })
      }
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink])

  const changeFontSize = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSize = e.target.value
      setFontSize(newSize)
      editor.dispatchCommand(SET_FONT_SIZE_COMMAND, newSize)
    },
    [editor]
  )

  const changeFontFamily = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newFont = e.target.value
      setFontFamily(newFont)
      editor.dispatchCommand(SET_FONT_FAMILY_COMMAND, newFont)
    },
    [editor]
  )

  const insertTable = () => {
    const rows = prompt('행 수를 입력하세요:', '3')
    const cols = prompt('열 수를 입력하세요:', '3')
    
    if (rows !== null && cols !== null) {
      const numRows = parseInt(rows, 10)
      const numCols = parseInt(cols, 10)
      
      if (!isNaN(numRows) && !isNaN(numCols) && numRows > 0 && numCols > 0) {
        editor.update(() => {
          const selection = $getSelection()
          const tableNode = $createTableNodeWithDimensions(numRows, numCols)
          selection?.insertNodes([tableNode])
        })
      }
    }
  }


  return (
    <div  className='toolbar'>
    
    <TabletDiv>

    <MobileDiv>
      <select
        className="toolbar-item spaced"
        onChange={changeFontFamily}
        value={fontFamily}
        title="글꼴"
        style={{ width: '120px' }}
      >
        {fontFamilies.map(({ label, value }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      <select
        className="toolbar-item spaced"
        onChange={changeFontSize}
        value={fontSize}
        title="글자 크기"
        style={{ width: '70px' }}
      >
        {fontSizes.map(({ label, size }) => (
          <option key={size} value={size}>
            {label}px
          </option>
        ))}
      </select>
      </MobileDiv>
      
      <MobileDiv>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
        }}
        className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
        title="굵게"
        type="button"
      >
        <i className="format bold" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
        }}
        className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
        title="기울임"
        type="button"
      >
        <i className="format italic" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
        }}
        className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
        title="밑줄"
        type="button"
      >
        <i className="format underline" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
        }}
        className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
        title="취소선"
        type="button"
      >
        <i className="format strikethrough" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
        }}
        className={'toolbar-item spaced ' + (isCode ? 'active' : '')}
        title="코드"
        type="button"
      >
        <i className="format code" />
      </button>
      {/* <button
        onClick={() => formatHeading('h1')}
        className="toolbar-item spaced heading"
        title="제목 1"
        type="button"
      >
        H1
      </button>
      <button
        onClick={() => formatHeading('h2')}
        className="toolbar-item spaced heading"
        title="제목 2"
        type="button"
      >
        H2
      </button>
      <button
        onClick={() => formatHeading('h3')}
        className="toolbar-item spaced heading"
        title="제목 3"
        type="button"
      >
        H3
      </button> */}
      <button
        onClick={insertHorizontalRule}
        className="toolbar-item spaced"
        title="구분선"
        type="button"
      >
        <i className="format">horizontal_rule</i>
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined)
        }}
        className="toolbar-item spaced"
        title="실행 취소"
        type="button"
      >
        <i className="format undo" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined)
        }}
        className="toolbar-item spaced"
        title="다시 실행"
        type="button"
      >
        <i className="format redo" />
      </button>
      </MobileDiv>
    </TabletDiv>
      
      <MobileDiv>
      <div className="divider"  />
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')
        }}
        className="toolbar-item spaced"
        title="왼쪽 정렬"
        type="button"
      >
        <i className="format">format_align_left</i>
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')
        }}
        className="toolbar-item spaced"
        title="가운데 정렬"
        type="button"
      >
        <i className="format">format_align_center</i>
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')
        }}
        className="toolbar-item spaced"
        title="오른쪽 정렬"
        type="button"
      >
        <i className="format">format_align_right</i>
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')
        }}
        className="toolbar-item spaced"
        title="양쪽 정렬"
        type="button"
      >
        <i className="format">format_align_justify</i>
      </button>
      <div className="divider" />
      <button
        onClick={insertLink}
        className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
        title="링크"
        type="button"
      >
        <i className="format">link</i>
      </button>
      <button
        onClick={insertTable}
        className="toolbar-item spaced"
        title="표 삽입"
        type="button"
      >
        <i className="format">table_chart</i>
      </button>
      </MobileDiv>
  
    </div>
  )
}

export default ToolbarPlugin 

const MobileDiv = styled.div`
  display: flex;
`

const TabletDiv = styled.div`
  display: flex;

   @media (max-width: 800px) {
    display: block;
  }
`